// src/app/core/services/notification.service.ts

import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import {
  ApiResponse,
  AppNotification,
  NotificationListResponse,
  UnreadCountResponse
} from '../models/notification';
import { BehaviorSubject, map, tap } from 'rxjs';
import { baseURL } from '../constants/base.url';

import { AuthenticationService } from './authentication.service';
import { SocketService } from './socket.service';

@Injectable({
  providedIn: 'root'
})
export class NotificationService {

  // 🔔 badge + dropdown state
  readonly unreadCount$ = new BehaviorSubject<number>(0);
  readonly recent$ = new BehaviorSubject<AppNotification[]>([]);

  // track karein ke socket listeners ek hi dafa attach hon
  private socketListenersAttached = false;

  constructor(
    private http: HttpClient,
    private authService: AuthenticationService,
    private socketSvc: SocketService
  ) {
    this.bootstrapSocketListeners();
  }

  // =========================
  // 🔌 SOCKET LISTENERS
  // =========================
  private bootstrapSocketListeners() {
    // AuthenticationService ke currentUser observable ko listen karein
    this.authService.currentUser.subscribe(user => {
      // logout / no user
      if (!user) {
        // optionally clear state
        this.recent$.next([]);
        this.unreadCount$.next(0);
        this.socketListenersAttached = false;
        return;
      }

      // user logged in hai
      if (this.socketListenersAttached) {
        // already attached, dobara nahi karna
        return;
      }
      this.socketListenersAttached = true;

      // ✅ yahan tak aate hi, AuthenticationService ne already:
      //   socketSvc.connect(accessToken) call kar diya hota hai

      // NEW notification
      this.socketSvc.onEvent<any>('notification:new').subscribe({
        next: (payload) => {
          console.log('[NotificationService] notification:new', payload);

          const current = this.recent$.value || [];
          const id = payload.id || payload._id;
          if (!id) return;

          const exists = current.some(n => n._id === id);
          if (exists) return;

          const mapped: AppNotification = {
            _id: id,
            type: payload.type,
            title: payload.title,
            message: payload.message || '',
            priority: payload.priority || 'NORMAL',
            isRead: !!payload.isRead,
            createdAt: payload.createdAt || new Date().toISOString(),
            readAt: payload.readAt || null,
            updatedAt: payload.updatedAt,
            meta: payload.meta
          };

          // newest notification top pe
          this.recent$.next([mapped, ...current]);
        },
        error: (err) => {
          console.warn('[NotificationService] notification:new listener error', err);
        }
      });

      // Unread count update
      this.socketSvc.onEvent<{ unreadCount: number }>('notification:unread-count').subscribe({
        next: (data) => {
          console.log('[NotificationService] notification:unread-count', data);
          this.unreadCount$.next(data?.unreadCount ?? 0);
        },
        error: (err) => {
          console.warn('[NotificationService] unread-count listener error', err);
        }
      });
    });
  }

  // =========================
  // 📥 HTTP APIs
  // =========================

  /**
   * Top-bar dropdown ke liye list load karo
   */
  loadRecent(page: number = 1, limit: number = 10, append = false) {
    const params = new HttpParams()
      .set('page', String(page))
      .set('limit', String(limit));

    return this.http
      .get<ApiResponse<NotificationListResponse>>(
        `${baseURL}notifications/me`,
        { params }
      )
      .pipe(
        map(res => res.data),
        tap(data => {
          if (append) {
            const current = this.recent$.value || [];
            const merged = [...current, ...data.notifications];

            // duplicates remove (safety)
            const unique = merged.filter(
              (n, idx, arr) => arr.findIndex(x => x._id === n._id) === idx
            );
            this.recent$.next(unique);
          } else {
            this.recent$.next(data.notifications);
          }
        })
      );
  }

  /**
   * Unread badge count refresh (page load pe)
   */
  refreshUnreadCount() {
    return this.http
      .get<ApiResponse<UnreadCountResponse>>(
        `${baseURL}notifications/me/unread-count`
      )
      .pipe(
        map(res => res.data.unreadCount),
        tap(count => this.unreadCount$.next(count))
      );
  }

  /**
   * Single notification ko read mark karo
   */
  markAsRead(id: string) {
    return this.http
      .post<ApiResponse<{ notification: AppNotification }>>(
        `${baseURL}notifications/me/${id}/read`,
        {}
      )
      .pipe(
        map(res => res.data.notification),
        tap(updated => {
          const list = this.recent$.value || [];
          const nextList = list.map(n => n._id === updated._id ? updated : n);
          this.recent$.next(nextList);
        })
      );
  }

  /**
   * Saari notifications ko read mark karo
   */
  markAllAsRead() {
    return this.http
      .post<ApiResponse<null>>(
        `${baseURL}notifications/me/read-all`,
        {}
      )
      .pipe(
        tap(() => {
          const list = this.recent$.value || [];
          const nextList = list.map(n => ({
            ...n,
            isRead: true,
            readAt: n.readAt || new Date().toISOString()
          }));
          this.recent$.next(nextList);
          this.unreadCount$.next(0);
        })
      );
  }
}